import { BaseAPI } from 'apis/amazon';
import { validateSortField } from 'slices/pagination.slice';
import {
  DatePeriodFilter,
  DurationFilter,
  FavoriteEnum,
  MediaType,
  SearchField,
  SearchType,
  SemanticSearchMethod,
  SemanticSortField,
} from 'utils/enum';
import { CustomDateParams } from 'utils/models';

export type GetLibraryParams = {
  // library params
  page_number?: number;
  page_size?: number;
  search_after?: unknown[];
  collection_id?: string[];
  sortBy?: string;
  order?: string;

  // advanced search params
  // collectionId?: string | null;
  isShowClipsOnly?: boolean;
  uploadDate?: DatePeriodFilter | null;
  customDate?: CustomDateParams | null;
  type?: MediaType | null;
  duration?: DurationFilter | null;
  speakerNames?: string[];

  // search params
  text?: string;
  searchField: string;
  searchValues: string[];
  [key: string]: string[] | any;
};

export type BucketInsight = {
  buckets: InsightKey[];
};
export type TrendingBucketInsight = {
  buckets: TrendingInsightKey[];
};

export type InsightKey = {
  collections: InsightBucket;
  media_ids: InsightBucket;
  doc_count: number;
  key: number;
  term_count: any;
};
export type TrendingInsightKey = {
  collections: InsightBucket;
  media_ids: InsightTrendingBucket;
  doc_count: number;
  key: number;
  term_count: any;
};

export type BaseBucket<T> = {
  buckets: T[];
  doc_count_error_upper_bound: number;
  sum_other_doc_count: number;
};

export type InsightBucket = BaseBucket<BucketKeyValue>;
export type InsightTrendingBucket = BaseBucket<TrendingBucketKeyValue>;

export type BucketKeyValue = {
  doc_count: number;
  key: string;
};

export type TrendingBucketKeyValue = {
  doc_count: number;
  key: string;
  thumbnail: string;
  title: string;
};

export type PostInsightResponse = {
  term_count_over_time: BucketInsight;
};
export type PostTrendingInsightResponse = {
  term_count_over_time: TrendingBucketInsight;
};

export type TermSearchInsight = {
  term: string;
} & PostInsightResponse;

export type TrendingTermSearchInsightResponse = {
  term: string;
  alias?: string;
  is_enabled?: boolean;
} & PostTrendingInsightResponse;

export type TrendingTermSearchInsight = {
  tagline?: string;
} & TrendingTermSearchInsightResponse;

export const LibraryService = {
  // GET /library
  getLibraryItems: (
    params: Omit<GetLibraryParams, 'searchField' | 'searchValues'>,
  ) => {
    const { search_after, ...rest } = params;
    return BaseAPI.get('/library', {
      params: {
        ...rest,
        sortBy: validateSortField(params.sortBy),
        ...(search_after ? { search_after: JSON.stringify(search_after) } : {}),
      },
    });
  },

  // POST /library
  postAdvancedSearch: (params: GetLibraryParams) => {
    return BaseAPI.post('/library', {
      ...params,
      sortBy: validateSortField(params.sortBy),
      [params.searchField]: (params?.searchValues ?? []) as string[],
    });
  },

  // POST /library/semantic-search
  postSemanticSearch: (
    params: {
      query: string;
      collection_ids: string[];
      limit: number;
      offset: number;
      sortBy?: SemanticSortField;
      order?: string;
      searchMethod?: SemanticSearchMethod;
      uploadDate: DatePeriodFilter | null;
      customDate: { startDate: Date; endDate: Date } | null;
      duration: DurationFilter | null;
      searchBy: SearchType.TITLE | null;
      type: MediaType | null;
      speakers: string[];
      languages: string[];
      excludeWords: string[];
      // next_search_after of the previous page; filter-only only.
      search_after?: unknown[];
    } & Partial<Record<SearchField, string[]>>,
  ) => {
    return BaseAPI.post('/library/semantic-search', params);
  },

  getFavoriteAction: () => {
    return BaseAPI.get('/favourite');
  },

  updateFavoriteAction: (payload: FavoriteEnum[]) => {
    return BaseAPI.post('/favourite', {
      favourite_items: payload,
    });
  },
};
